<template>
    <span
        ref="textElement"
        class="typewriter-text"
        :class="{ 'show-cursor': showCursor }"
        v-html="formattedText"
    ></span>
</template>

<script>
export default {
    name: 'TypewriterText',
    props: {
        text: {
            type: String,
            default: '',
        },
        speed: {
            type: Number,
            default: 50, // milliseconds per character
        },
        showCursor: {
            type: Boolean,
            default: true,
        },
        autoStart: {
            type: Boolean,
            default: true,
        },
        cursorChar: {
            type: String,
            default: '|',
        },
    },
    data() {
        return {
            displayText: '',
            isAnimating: false,
            timeoutId: null,
        }
    },
    computed: {
        duration() {
            if (!this.text) return 0
            // Count total words across all paragraphs
            const wordCount = this.text
                .split(/\n\n|\n/)
                .filter(paragraph => paragraph.trim())
                .reduce((total, paragraph) => total + paragraph.split(' ').length, 0)
            return wordCount * this.speed * 3 // Match the delay multiplier
        },
        charCount() {
            return this.text.length
        },
        formattedText() {
            if (!this.text) return ''

            // Split by paragraphs first, then words, preserving line breaks
            let wordIndex = 0
            return this.text
                .split(/\n\n|\n/)
                .map(paragraph => {
                    if (!paragraph.trim()) {
                        return '<br>' // Empty line becomes a line break
                    }

                    const words = paragraph.split(' ').map(word => {
                        const delay = wordIndex * this.speed * 3
                        wordIndex++
                        return `<span class="word" style="animation-delay: ${delay}ms">${word}</span>`
                    })

                    return words.join(' ')
                })
                .join('<br><br>') // Join paragraphs with double line breaks
        },
    },
    watch: {
        text: {
            immediate: true,
            handler(newText) {
                if (newText && this.autoStart) {
                    this.startAnimation()
                }
            },
        },
    },
    mounted() {
        if (this.text && this.autoStart) {
            this.startAnimation()
        }
    },
    methods: {
        startAnimation() {
            if (this.isAnimating || !this.text || !this.$refs.textElement) return

            this.isAnimating = true

            // Start the CSS animation
            const element = this.$refs.textElement
            if (element) {
                element.classList.add('animate')
            }

            // Emit complete after the CSS animation duration
            setTimeout(() => {
                this.isAnimating = false
                this.$emit('complete')
            }, this.duration)
        },

        reset() {
            this.isAnimating = false
            this.displayText = ''
            if (this.$refs.textElement) {
                this.$refs.textElement.classList.remove('animate')
            }
        },

        restart() {
            this.reset()
            this.$nextTick(() => {
                if (this.$refs.textElement) {
                    this.startAnimation()
                }
            })
        },
    },
}
</script>

<style scoped>
.typewriter-text {
    display: inline-block;
    position: relative;
    word-wrap: break-word;
    white-space: normal;
}

.typewriter-text :deep(.word) {
    opacity: 0;
    display: inline;
}

.typewriter-text.animate :deep(.word) {
    animation: reveal 0.3s ease-in-out forwards;
}

.typewriter-text.show-cursor::after {
    content: '|';
    display: inline;
    animation: blink 1s infinite;
    margin-left: 2px;
}

@keyframes reveal {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}
</style>